/**
 * Avatar shape support — an emoji silhouette used as an alpha mask
 * over a user's avatar. Mirrors the Ditto kind-0 `shape` extension.
 */

export type AvatarShape = string;

/**
 * Heuristic emoji check: short, contains non-ASCII. Catches keycaps,
 * flags, and ZWJ sequences without trying to enumerate Unicode ranges.
 */
export function isEmoji(value: string): boolean {
  if (!value || value.length === 0) return false;
  if (value.length > 20) return false;
  // eslint-disable-next-line no-control-regex
  return /[^\x00-\x7F]/.test(value);
}

export function isValidAvatarShape(value: unknown): value is AvatarShape {
  if (typeof value !== 'string' || value.length === 0) return false;
  return isEmoji(value);
}

/** Extracts a valid AvatarShape from kind-0 metadata. Returns undefined for "circle". */
export function getAvatarShape(metadata: { [key: string]: unknown } | undefined): AvatarShape | undefined {
  const raw = metadata?.shape;
  return isValidAvatarShape(raw) ? raw : undefined;
}

/** In-memory cache: emoji string → data-URL. */
const emojiMaskCache = new Map<string, string>();

/**
 * Renders the user's native OS emoji onto a canvas and returns a PNG
 * data-URL alpha mask suitable for use as a CSS `mask-image`.
 *
 * 1. Draw at 512px on an oversized scratch canvas.
 * 2. Scan for the tight non-transparent bounding box.
 * 3. Square the crop so non-square emoji don't get stretched.
 * 4. Redraw into a 256×256 output canvas.
 * 5. Convert to white-on-original-alpha.
 */
export function getEmojiMaskUrl(emoji: string): string {
  const cached = emojiMaskCache.get(emoji);
  if (cached) return cached;

  const fontSize = 512;
  const scratch = fontSize * 1.5;
  const c1 = document.createElement('canvas');
  c1.width = scratch;
  c1.height = scratch;
  const ctx1 = c1.getContext('2d');
  if (!ctx1) return '';

  ctx1.textAlign = 'center';
  ctx1.textBaseline = 'middle';
  ctx1.font = `${fontSize}px serif`;
  ctx1.fillText(emoji, scratch / 2, scratch / 2);

  // Alpha threshold ignores anti-alias fringe / drop-shadow halos that
  // would otherwise inflate the bounding box and offset the crop.
  const ALPHA_THRESHOLD = 25;
  const { data: px, width: sw, height: sh } = ctx1.getImageData(0, 0, scratch, scratch);
  let t = sh, b = 0, l = sw, r = 0;
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      if (px[(y * sw + x) * 4 + 3] > ALPHA_THRESHOLD) {
        if (y < t) t = y;
        if (y > b) b = y;
        if (x < l) l = x;
        if (x > r) r = x;
      }
    }
  }
  if (r < l || b < t) return '';

  let cropW = r - l + 1;
  let cropH = b - t + 1;
  if (cropW > cropH) {
    const diff = cropW - cropH;
    t -= Math.floor(diff / 2);
    cropH = cropW;
  } else if (cropH > cropW) {
    const diff = cropH - cropW;
    l -= Math.floor(diff / 2);
    cropW = cropH;
  }
  if (t < 0) t = 0;
  if (l < 0) l = 0;

  const out = 256;
  const c2 = document.createElement('canvas');
  c2.width = out;
  c2.height = out;
  const ctx2 = c2.getContext('2d');
  if (!ctx2) return '';

  ctx2.drawImage(c1, l, t, cropW, cropH, 0, 0, out, out);

  const img = ctx2.getImageData(0, 0, out, out);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 255;
    d[i + 1] = 255;
    d[i + 2] = 255;
  }
  ctx2.putImageData(img, 0, 0);

  const url = c2.toDataURL('image/png');
  emojiMaskCache.set(emoji, url);
  return url;
}
